// Parent Progress tab: Sunny Speech Progress — per sound: stage bars for the
// full 10-step ladder, Beginning/Middle/End performance, independent vs
// Pip-supported turns, 4-week trend and a suggested next practice tip.
// Plain-language for parents, never one flattened percentage.
import { useEffect, useState } from "react";
import { AudioLines, Lightbulb } from "lucide-react";
import { api } from "../../lib/api";

const STAGE_LABELS = {
  listen: "Listen", see_how: "See how", sound: "Sound", syllable: "Syllables",
  word: "Words", phrase: "Phrases", sentence: "Sentences", story: "Story",
  conversation: "Conversation", carryover: "Everyday talk",
};
const STATUS_COLOR = {
  not_started: "bg-slate-200", learning: "bg-sky-400",
  practicing: "bg-amber-400", proficient: "bg-emerald-400",
  mastered: "bg-emerald-600",
};
const STATUS_LABEL = {
  not_started: "Not started", learning: "Learning", practicing: "Practicing",
  proficient: "Proficient", mastered: "Mastered",
};

const StageBar = ({ stages, childId, cid }) => (
  <div className="flex gap-1" data-testid={`speech-stagebar-${childId}-${cid}`}>
    {stages.map((s) => (
      <div key={s.key} className="flex-1" title={`${STAGE_LABELS[s.key]}: ${STATUS_LABEL[s.status]}`}>
        <div className={`h-2.5 rounded-full ${STATUS_COLOR[s.status]}`} />
      </div>
    ))}
  </div>
);

const SoundRow = ({ childId, sound }) => (
  <div className="rounded-xl border border-border p-3 space-y-2"
    data-testid={`speech-progress-sound-${childId}-${sound.content_id}`}>
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <span className="text-sm font-bold">
        {sound.sound} <span className="text-muted-foreground font-medium">— {sound.title}</span>
      </span>
      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full text-white ${STATUS_COLOR[sound.overall_stage] || "bg-slate-400"}`}>
        {STATUS_LABEL[sound.overall_stage] || sound.overall_stage}
      </span>
    </div>
    <StageBar stages={sound.stages} childId={childId} cid={sound.content_id} />
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
      <span>Now at: <b>{STAGE_LABELS[sound.current_stage]}</b></span>
      {["beginning", "middle", "end"].map((p) => (
        sound.positions[p] !== "not_started" && (
          <span key={p}>{p[0].toUpperCase() + p.slice(1)} of words:{" "}
            <b>{STATUS_LABEL[sound.positions[p]]}</b></span>
        )
      ))}
      <span>On their own: <b>{sound.independent_turns}</b> tries
        {sound.supported_turns > 0 && <> · with Pip's help: <b>{sound.supported_turns}</b></>}
      </span>
    </div>
  </div>
);

export const SpeechProgressCard = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    api.get("/children").then(async ({ data }) => {
      const kids = data.children || data || [];
      const out = [];
      for (const k of kids) {
        try {
          const { data: rep } = await api.get(`/speech/children/${k.child_id}/progress-report`);
          if (rep.sounds?.length) out.push({ child: k, report: rep });
        } catch { /* child without speech practice yet */ }
      }
      if (live) setRows(out);
    }).catch(() => live && setRows([]));
    return () => { live = false; };
  }, []);
  if (rows === null) {
    return <section className="bg-card rounded-2xl border border-border p-5 animate-pulse h-24"
      data-testid="speech-progress-loading" />;
  }
  if (!rows.length) return null;
  return (
    <section className="bg-card rounded-2xl border border-border p-5"
      data-testid="speech-progress-card">
      <h2 className="text-base md:text-lg font-bold font-display flex items-center gap-2 mb-1">
        <AudioLines className="h-5 w-5 text-violet-600" aria-hidden="true" />
        Sunny Speech Progress
      </h2>
      <p className="text-xs text-muted-foreground mb-3">
        Each sound moves through ten steps — from hearing it to using it in everyday talk.
      </p>
      <div className="space-y-4">
        {rows.map(({ child, report }) => (
          <div key={child.child_id} className="space-y-2"
            data-testid={`speech-progress-${child.child_id}`}>
            <p className="text-sm font-extrabold">{child.display_name || child.name}</p>
            {report.sounds.map((s) => (
              <SoundRow key={s.content_id} childId={child.child_id} sound={s} />
            ))}
            {report.suggested_next && (
              <p className="flex items-start gap-2 text-xs font-medium rounded-xl bg-violet-50 border border-violet-200 px-3 py-2 text-violet-900"
                data-testid={`speech-progress-tip-${child.child_id}`}>
                <Lightbulb className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                {report.suggested_next.message}
              </p>
            )}
            {report.trend?.length > 0 && (
              <p className="text-[11px] text-muted-foreground"
                data-testid={`speech-progress-trend-${child.child_id}`}>
                Recent practice: {report.trend.map((t) =>
                  `${t.week.split("-")[1]} — ${t.attempts} tries (${t.strong} strong)`).join(" · ")}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
